/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
package auth

import kotlinx.datetime.Clock
import kotlin.random.Random

/**
 * Manages persistent user identification across app sessions
 * Stores the authenticated user ID from the server
 */
expect class UserIdManager {
    /**
     * Get the authenticated user ID
     * Returns stored userId from login, or generates a temporary one if not authenticated
     */
    fun getUserId(): String

    /**
     * Set the authenticated user ID (called after successful login)
     */
    fun setUserId(userId: String)

    /**
     * Clear the stored user ID (for logout)
     */
    fun clearUserId()
}

/**
 * In-memory override for the no-account demo. When a demo session is active this
 * holds the demo account's userId (Alex). Every UserIdManager actual returns it
 * from getUserId() ahead of the persisted value, so user-scoped local data
 * (goals, budget, advice, recommendations) reads the demo account — NOT the real
 * user's last-login data still sitting in UserDefaults / localStorage. It is
 * never persisted, so clearing it (on demo exit) instantly restores the real
 * user's identity with no clobber.
 */
object DemoUserOverride {
    var userId: String? = null

    // The demo JWT. Some data layers (e.g. the iOS savings-goals factory) read
    // the auth token straight from persistent storage rather than the in-memory
    // session; during a demo they must use this token instead so their reads hit
    // the demo account. Never persisted.
    var authToken: String? = null

    val isActive: Boolean get() = userId != null
}

/**
 * Generate a unique user ID
 * This is a fallback for unauthenticated state
 * In production, userId should come from authentication system
 */
fun generateUserId(): String {
    val timestamp = Clock.System.now().toEpochMilliseconds()
    val random = Random.nextInt(10000, 99999)
    return "user-$timestamp-$random"
}
